export const COUNTRY_ALIASES: Record<string, string> = {
  usa: "United States",
  "u.s.a.": "United States",
  us: "United States",
  "u.s.": "United States",
  uk: "United Kingdom",
  "u.k.": "United Kingdom",
};

export const cleanString = (value: unknown): any =>
  typeof value === "string" ? value.trim().replace(/^"+|"+$/g, "") : value;

export interface FormSnapshot {
  spotifyUri?: string;
  asin?: string;
  appleId?: string;
  artistHometown?: string;
  artistCurrentTown?: string;
  // Apple asks for the artist's country; TIDAL doesn't.
  appleCountry?: string;
  distributor?: string;
  TIDALpage?: string;
  soundCloudProfileUrl?: string;
  soundCloudLabel?: string;
  language?: string[];
  socialsLinks?: string;
  similarArtists?: string;
  listenersLocation?: string[];
}

export interface RosterArtist {
  id?: string;
  spotifyURI?: string;
  asin?: string;
  appleID?: string;
  hometown?: string;
  currentTown?: string;
  country?: string;
  distributor?: string;
  tidalURL?: string;
  soundcloudURL?: string;
  label?: string;
  lyricLanguage?: string;
  instagram?: string;
  tiktok?: string;
  twitter?: string;
  facebook?: string;
  snapchat?: string;
  similarArtists?: string;
  listenersLocation?: string;
}

export interface AutofillOptions {
  countryDropdownOptions: string[];
  languageDropdownOptions: string[];
  listenersLocationOptions: string[];
}

export type AutofillPatch = Partial<FormSnapshot>;

/** Field names this module is capable of autofilling. */
export type AutofillField = keyof FormSnapshot;

/**
 * Fields previously written by autofill (i.e. owned by the last-selected
 * artist rather than typed by the user). Passing this set lets a subsequent
 * artist selection replace stale values from the previous artist.
 */
export type OwnedFields = ReadonlySet<AutofillField>;

/**
 * Pure function: given the current form values, a roster artist, and the set
 * of valid country/language dropdown options, returns the patch of fields to
 * setFieldValue on. Never returns keys for fields that should remain untouched.
 *
 * Honors the "don't overwrite user input" rule for ALL fields — including
 * the country, which previously wiped user input when the artist's country
 * didn't match a dropdown option (HBM-autofill bug fix).
 *
 * `owned` names the fields the PREVIOUS autofill filled in. Those are treated
 * as replaceable, so switching from artist A to artist B swaps A's values out
 * instead of leaving them stranded (a field holding A's data is not empty, so
 * the plain "only fill blanks" rule would skip it forever). Fields the user
 * typed are never in `owned` and stay protected.
 */
export const computeArtistAutofill = (
  current: FormSnapshot,
  artist: RosterArtist,
  options: AutofillOptions,
  owned: OwnedFields = new Set<AutofillField>(),
): AutofillPatch => {
  const patch: AutofillPatch = {};

  // A field is writable when the user has not put anything there, or when what
  // is there came from the previously selected artist.
  const isEmpty = (value: unknown) =>
    value === undefined || value === null || value === "" ||
    (Array.isArray(value) && value.length === 0);
  const canWrite = (field: AutofillField) =>
    isEmpty(current[field]) || owned.has(field);

  if (artist.spotifyURI && canWrite("spotifyUri")) {
    patch.spotifyUri = cleanString(artist.spotifyURI);
  }
  if (artist.asin && canWrite("asin")) {
    patch.asin = cleanString(artist.asin);
  }
  if (artist.appleID && canWrite("appleId")) {
    patch.appleId = cleanString(artist.appleID);
  }
  if (artist.hometown && canWrite("artistHometown")) {
    patch.artistHometown = cleanString(artist.hometown);
  }
  if (artist.currentTown && canWrite("artistCurrentTown")) {
    patch.artistCurrentTown = cleanString(artist.currentTown);
  }

  // Country: ONLY autofill if the artist has a country AND the field is empty
  // AND the country matches a dropdown option. Never wipe existing user input.
  if (artist.country && canWrite("appleCountry")) {
    const normalized = cleanString(artist.country).toLowerCase();
    const mapped = COUNTRY_ALIASES[normalized] || cleanString(artist.country);
    const matched = options.countryDropdownOptions.find(
      (c) => c.trim().toLowerCase() === String(mapped).trim().toLowerCase(),
    );
    if (matched) {
      patch.appleCountry = matched;
    }
  }

  if (artist.distributor && canWrite("distributor")) {
    patch.distributor = cleanString(artist.distributor);
  }
  if (artist.tidalURL && canWrite("TIDALpage")) {
    patch.TIDALpage = cleanString(artist.tidalURL);
  }
  if (artist.soundcloudURL && canWrite("soundCloudProfileUrl")) {
    patch.soundCloudProfileUrl = cleanString(artist.soundcloudURL);
  }
  if (artist.label && canWrite("soundCloudLabel")) {
    patch.soundCloudLabel = cleanString(artist.label);
  }

  if (artist.lyricLanguage && canWrite("language")) {
    const lang = cleanString(artist.lyricLanguage);
    const matchedLang = options.languageDropdownOptions.find(
      (l) => l.trim().toLowerCase() === String(lang).trim().toLowerCase(),
    );
    if (matchedLang) {
      patch.language = [matchedLang];
    }
  }

  const socialLinks = [
    artist.instagram && `Instagram: ${cleanString(artist.instagram)}`,
    artist.tiktok && `TikTok: ${cleanString(artist.tiktok)}`,
    artist.twitter && `Twitter: ${cleanString(artist.twitter)}`,
    artist.facebook && `Facebook: ${cleanString(artist.facebook)}`,
    artist.snapchat && `Snapchat: ${cleanString(artist.snapchat)}`,
  ]
    .filter(Boolean)
    .join(", ");

  if (socialLinks && canWrite("socialsLinks")) {
    patch.socialsLinks = socialLinks;
  }

  // similarArtists: roster stores comma-separated, pitch form uses " || " separator
  if (artist.similarArtists && canWrite("similarArtists")) {
    const artists = cleanString(artist.similarArtists)
      .split(",")
      .map((a: string) => a.trim())
      .filter((a: string) => a)
      .slice(0, 3);
    if (artists.length > 0) {
      patch.similarArtists = artists.join(" || ");
    }
  }

  // listenersLocation: roster stores a single string, pitch form expects string[]
  // Only include values that match valid dropdown options
  if (artist.listenersLocation && canWrite("listenersLocation")) {
    const locations = cleanString(artist.listenersLocation)
      .split(",")
      .map((l: string) => l.trim())
      .filter((l: string) => l);
    const matched = locations.filter((l: string) =>
      options.listenersLocationOptions.find(
        (opt) => opt.trim().toLowerCase() === l.toLowerCase(),
      ),
    );
    if (matched.length > 0) {
      patch.listenersLocation = matched;
    }
  }

  // Clear any field the previous artist owned that this artist cannot supply,
  // so switching artists never strands the old artist's data in the form.
  // Array-typed fields reset to [] rather than "" to keep their shape.
  const ARRAY_FIELDS = new Set<AutofillField>(["language", "listenersLocation"]);
  for (const field of Array.from(owned)) {
    if (field in patch) continue;
    if (isEmpty(current[field])) continue;
    (patch as Record<string, unknown>)[field] = ARRAY_FIELDS.has(field) ? [] : "";
  }

  return patch;
};
